import {
  Children,
  isValidElement,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { Avatar, Checkbox, Dropdown, Space, Table } from 'antd'
import { DownOutlined } from '@ant-design/icons'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'

import styled from 'styled-components'
import { colours, sizes } from '@qflow/theme'

import {
  useCheckboxStates,
  useModuleIds,
  useUserProjectRole
} from 'portal/lib/hooks'
import {
  ColourButton,
  icons,
  SmallSpinner,
  Spacer,
  Spinner,
  UserAvatar
} from 'portal/lib/primitives'
import {
  projectUsersLoad,
  PROJECT_USERS_ABORT
} from 'portal/store/projectUsers/actions'
import {
  uiSetDeliveryTableColumns,
  uiSetWasteTableColumns
} from 'portal/store/ui/actions'

import { ActionStrip } from './ActionStrip'
import './RecordsTable.scss'

const RecordsTable = ({
  id,
  columns,
  data,
  handlePageChange,
  loading,
  count,
  showTotal,
  updateFilters,
  onChange,
  hasNextPage,
  footer
}) => {
  const dispatch = useDispatch()
  const { accountId, projectId, moduleName } = useModuleIds()
  const { isProjectUser, isProjectAdmin, roleLoaded } = useUserProjectRole(
    accountId,
    projectId
  )

  // Set columns in Redux UI store,
  // filtering out columns that are not user settable
  // (i.e. dataIndex is undefined or null)
  // and only store dataIndex and title
  useEffect(() => {
    const getPossibleAltValue = titleElement => {
      // Check if the titleElement has an alt prop
      if (titleElement.props && titleElement.props.alt) {
        return titleElement.props.alt
      }
      // If not, check its children for an alt prop
      if (titleElement.props && titleElement.props.children) {
        let found = null
        Children.forEach(titleElement.props.children, child => {
          if (found) return // Stop if already found

          if (isValidElement(child)) {
            // Check if this element has an alt prop
            if (child.props.alt) {
              found = (
                <span>
                  {child.props.alt} {child}
                </span>
              )
              return
            }
          }
        })

        if (found) {
          return found
        }
      }
      return titleElement
    }
    const filteredAndReducedColumns = columns
      .filter(col => col.dataIndex)
      .map(col => ({
        dataIndex: col.dataIndex,
        title: isValidElement(col.title)
          ? getPossibleAltValue(col.title)
          : col.title
      }))
    dispatch(
      moduleName.toLowerCase() === 'delivery'
        ? uiSetDeliveryTableColumns(filteredAndReducedColumns)
        : uiSetWasteTableColumns(filteredAndReducedColumns)
    )
  }, [columns, dispatch, moduleName])

  const [
    checkedRecordIds,
    setCheckedRecordId,
    setBulkCheckedRecordId,
    clearCheckedRecords
  ] = useCheckboxStates()

  const [indeterminate, setIndeterminate] = useState(false)
  const [checkAll, setCheckAll] = useState(false)
  const [recordAssigneeMapping, setRecordAssigneeMapping] = useState({})
  const [selectedRecordsContainUnloaded, setSelectedRecordsContainUnloaded] =
    useState(false)
  const shownTableColumns = useSelector(
    state => state.ui.data[`${moduleName.toLowerCase()}ShownTableColumns`]
  )
  const orderedTableColumns = useSelector(
    state => state.ui.data[`${moduleName.toLowerCase()}OrderedTableColumns`]
  )

  const allRecordIds = useSelector(
    state => state.records.byModule[moduleName.toLowerCase()].allRecordIds
  )

  // Each data item returns assignedTo as an array of users
  // We will flatten this to a single comma separate string of Usernames
  // to show in the table
  const formattedData = useMemo(() => {
    const formattedData = []

    data.forEach(dataItem => {
      if (dataItem.record && dataItem.record.assignedTo) {
        const userAvatars = dataItem.record.assignedTo.map(user => (
          <UserAvatar key={user.userId} displayName={user.userName} />
        ))
        formattedData.push({
          ...dataItem,
          assignedTo: <Avatar.Group>{userAvatars}</Avatar.Group>
        })
      } else {
        formattedData.push({
          ...dataItem
        })
      }
    })

    return formattedData
  }, [data])

  const menuItems = [
    { label: 'Select all', key: 'selectAll' },
    {
      label: `Select ${formattedData.length - 1} records`,
      key: 'selectAllLoaded'
    },
    { label: 'Deselect all', key: 'deselectAll' }
  ]

  const handleSelectMenuClick = e => {
    switch (e.key) {
      // Select all records including ones not currently loaded
      case 'selectAll':
        setCheckAll(true)
        setBulkCheckedRecordId(allRecordIds, true)
        break
      case 'selectAllLoaded': {
        // Select currently loaded/displayed records that are not closed
        setBulkCheckedRecordId(
          data
            .filter(item => item.status !== 'Closed' && item.recordIdRaw !== 1)
            .map(item => item.recordIdRaw),
          true
        )
        break
      }
      case 'deselectAll':
        clearCheckedRecords()
        break
    }
  }

  // Order columns based on orderedTableColumns state, if it exists, if not check localStorage
  // If neither exist, return allColumns as is
  const orderColumns = useCallback(
    allColumns => {
      const storedOrderedColumns = localStorage.getItem(
        `${moduleName.toLowerCase()}-orderedTableColumns`
      )
      const usingStoredColumns =
        orderedTableColumns && orderedTableColumns.length > 0
          ? orderedTableColumns
          : storedOrderedColumns
            ? JSON.parse(storedOrderedColumns)
            : null

      if (usingStoredColumns) {
        const columnMap = {}
        let unknownCount = 0
        allColumns.forEach(col => {
          if (col.dataIndex) {
            columnMap[col.dataIndex] = col
          } else {
            // Handle columns without a dataIndex (e.g. RecordLink column)
            columnMap[`unknown-${unknownCount}`] = col
            unknownCount += 1
          }
        })
        const orderedCols = []
        usingStoredColumns.forEach(colId => {
          if (columnMap[colId]) {
            orderedCols.push(columnMap[colId])
            delete columnMap[colId]
          }
        })
        // Add any columns that were not included in the orderedTableColumns to the end
        Object.keys(columnMap).forEach(colId => {
          orderedCols.push(columnMap[colId])
        })
        return orderedCols
      }
      return allColumns
    },
    [moduleName, orderedTableColumns]
  )

  let updatedColumns = []

  // Add selection column if Project Admin
  if (isProjectAdmin) {
    const selectionColumn = {
      title: '',
      dataIndex: 'recordIdRaw',
      className: 'recordIdRaw',
      width: '60px',
      render: (text, record, index) =>
        index === 0 ? (
          allRecordIds && allRecordIds.length > 0 ? (
            <Dropdown
              menu={{ items: menuItems, onClick: handleSelectMenuClick }}
            >
              <Space>
                <Checkbox
                  style={{ pointerEvents: 'none' }}
                  indeterminate={indeterminate}
                  checked={checkAll}
                ></Checkbox>
                <DownOutlined />
              </Space>
            </Dropdown>
          ) : (
            ''
          )
        ) : (
          <Checkbox
            checked={checkedRecordIds.byKey[text]}
            onChange={e => {
              setCheckedRecordId(text, e.target.checked)
            }}
            disabled={record.status === 'Closed'}
          />
        )
    }
    updatedColumns = [selectionColumn, ...orderColumns(columns)]
  } else {
    updatedColumns = [...orderColumns(columns)]
  }

  // Added Hidden properties to columns based on shownTableColumns state
  updatedColumns = updatedColumns.map(col => {
    if (
      col.dataIndex &&
      col.dataIndex !== 'recordIdRaw' &&
      shownTableColumns &&
      !shownTableColumns.includes(col.dataIndex)
    ) {
      return { ...col, hidden: true }
    }
    return col
  })

  // Clear checked records if module changes
  useEffect(() => {
    clearCheckedRecords()
  }, [clearCheckedRecords])

  // Update select all indicator based on number selected
  useEffect(() => {
    // Filter out the Select All checkbox so it's not included in the count
    const filteredCheckedRecordIds = checkedRecordIds.list.filter(
      id => id.length > 1
    )
    if (filteredCheckedRecordIds.length === 0) {
      setIndeterminate(false)
      setCheckAll(false)
    } else if (filteredCheckedRecordIds.length < count) {
      setIndeterminate(true)
      setCheckAll(false)
    } else {
      setIndeterminate(false)
      setCheckAll(true)
    }
  }, [checkedRecordIds, count])

  // Create an object from the returned records that uses the recordId as the key
  // and an array of who is assigned to that record
  useEffect(() => {
    const tempMapping = {}
    data.forEach(dataItem => {
      if (dataItem.record) {
        tempMapping[dataItem.recordIdRaw] = dataItem.record.assignedTo || []
      }
    })
    setRecordAssigneeMapping(tempMapping)
  }, [data])

  // Check if 1 or more checkedRecordIds is not in loaded data
  useEffect(() => {
    const loadedDataIds = data.map(item => item.recordIdRaw)
    setSelectedRecordsContainUnloaded(
      checkedRecordIds.list.some(element => {
        return !loadedDataIds.includes(element)
      })
    )
  }, [checkedRecordIds, data])

  // Load Project Users in to Redux state
  useEffect(() => {
    if (roleLoaded && isProjectUser) {
      dispatch(projectUsersLoad(accountId, projectId, false))
    }

    // Using useEffect return PROJECT_USERS_ABORT to cancel any ongoing requests
    return () => {
      dispatch({ type: PROJECT_USERS_ABORT })
    }
  }, [accountId, dispatch, isProjectUser, projectId, roleLoaded])

  // Infinite Scroll
  useEffect(() => {
    const handleTableScroll = event => {
      const maxScroll = event.target.scrollHeight - event.target.clientHeight
      const currentScroll = event.target.scrollTop
      if (currentScroll >= maxScroll - 65) {
        // load more data
        handlePageChange()
      }
    }

    const debouncedHandleTableScroll = debounce(
      event => handleTableScroll(event),
      100,
      {
        leading: true
      }
    )

    const tableContent = document.querySelector('.ant-table-body')
    if (tableContent) {
      // Only apply the Event Listener if there is more data to be loaded
      if (hasNextPage) {
        tableContent.addEventListener('scroll', debouncedHandleTableScroll)
      }
    }

    // Using useEffect return function to remove the Event Listener on unload
    return () => {
      if (tableContent) {
        tableContent.removeEventListener('scroll', debouncedHandleTableScroll)
      }
    }
  }, [handlePageChange, hasNextPage])

  const scrollToTop = () => {
    const tableContent = document.querySelector('.ant-table-body')
    tableContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  let usedHeight = 365
  const beamerAnnouncementBar = document.getElementById('beamerAnnouncementBar')
  if (beamerAnnouncementBar) {
    usedHeight += beamerAnnouncementBar.getBoundingClientRect().height
  }

  return (
    <>
      <ActionStrip
        recordIds={checkedRecordIds.list.filter(id => id.length > 1)}
        recordAssigneeMapping={recordAssigneeMapping}
        selectedRecordsContainUnloaded={selectedRecordsContainUnloaded}
        onClose={clearCheckedRecords}
        onApply={updateFilters}
      />

      {(!loading || formattedData.length > 100) &&
        shownTableColumns &&
        shownTableColumns.length > 0 && (
          <>
            <div
              id={id || 'RecordsTable'}
              style={{ position: 'relative' }}
              data-testid="records-table"
            >
              <TableWrapper>
                <Table
                  class="recordsTable"
                  bordered={false}
                  rowClassName={(record, index) => {
                    if (index === 0) {
                      if (isProjectUser) {
                        return 'table-row-filter'
                      }
                      // Hide Filter Row for non-project users
                      return 'table-row-filter-hide'
                    }
                    return 'table-row-light'
                  }}
                  onRow={(record, rowIndex) => {
                    if (rowIndex === 0) {
                      return {}
                    }
                    return {
                      'data-testid': 'RecordsTableRow'
                    }
                  }}
                  dataSource={formattedData}
                  rowKey={row => row.recordIdRaw}
                  columns={updatedColumns}
                  scroll={{ y: `calc(100vh - ${usedHeight}px)` }}
                  size="middle"
                  sticky
                  pagination={false}
                  onChange={onChange}
                  footer={footer ? () => footer : null}
                  loading={{
                    spinning: loading,
                    indicator: (
                      <div>
                        <SmallSpinner />
                      </div>
                    )
                  }}
                />
              </TableWrapper>
            </div>
            {!footer && (
              <TableFooter>
                {showTotal()}
                <ButtonContainer>
                  <BTTButton id="BTTButton" onClick={scrollToTop}>
                    Back to top
                    <Spacer.Fine />
                    <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
                  </BTTButton>
                </ButtonContainer>
              </TableFooter>
            )}
          </>
        )}
      {(!shownTableColumns ||
        shownTableColumns.length < 1 ||
        (loading && formattedData.length < 101)) && <Spinner />}
    </>
  )
}

const TableWrapper = styled.div`
  background-color: ${colours.WHITE};
  border-radius: 10px;
  padding: 0.5rem 1.25rem 1.25rem 1.25rem;
  margin-top: 1rem;
  box-shadow: 0px 1px 3px 0px #00000026;
`
const TableFooter = styled('div')`
  position: relative;
  width: 99%;
`

const ButtonContainer = styled('div')`
  position: absolute;
  right: 0;
  top: 0.75rem;
`
const BTTButton = styled(ColourButton.Basic)`
  line-height: 1;
  width: auto;
`

RecordsTable.propTypes = {
  id: PropTypes.string,
  data: PropTypes.arrayOf(PropTypes.object),
  handlePageChange: PropTypes.func,
  columns: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  pageSize: PropTypes.number,
  current: PropTypes.number,
  showTotal: PropTypes.func
}

export default RecordsTable
